import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { Plus, Truck, PackageCheck, PackagePlus, Clock } from 'lucide-react';
import { PageHeader, KpiCard, StatusChip, Panel, FormDialog, LoadGate, PageShell, CHART_FONT, DIALOG_PAPER_SX } from './businessUi';
import { formatCurrency } from './erpData';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';

const KEYS = { orders: ERP_KEYS.purchase };
const statusTone = { Received: 'success', 'Partially Received': 'warning', Open: 'secondary', Cancelled: 'destructive' };
const STATUS_COLORS = { Received: '#10b981', 'Partially Received': '#f59e0b', Open: '#0ea5e9', Cancelled: '#f43f5e' };

const ADD_FIELDS = [
  { name: 'supplier', label: 'Supplier', required: true },
  { name: 'date', label: 'PO Date', type: 'date', default: '2026-06-13' },
  { name: 'items', label: 'Line Items', type: 'number', default: 1 },
  { name: 'amount', label: 'PO Value (INR)', type: 'number', default: 0 },
  { name: 'status', label: 'Status', type: 'select', default: 'Open', options: ['Open', 'Partially Received', 'Received', 'Cancelled'] },
];

// Total value already received against a PO, summed from its dated receipt history.
const receivedOf = (o) => (o.receipts || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
// Status follows the receipts: nothing = Open, some = Partially Received, full = Received.
// POs with no receipt history yet keep their stored status (backward compatible).
const deriveStatus = (o) => {
  if (o.status === 'Cancelled') return 'Cancelled';
  if (!o.receipts || o.receipts.length === 0) return o.status;
  const rec = receivedOf(o);
  const amt = Number(o.amount) || 0;
  if (rec <= 0) return 'Open';
  return rec < amt ? 'Partially Received' : 'Received';
};

export default function Purchase() {
  const { data, loading, error, save, reload } = useErp(KEYS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [receiveIndex, setReceiveIndex] = useState(null);
  const [historyId, setHistoryId] = useState(null);

  const purchaseOrders = data?.orders || [];
  const today = new Date().toISOString().slice(0, 10);

  const handleAdd = (v) => {
    const next = [{ id: `PO-${7700 + purchaseOrders.length + 1}`, receipts: [], ...v }, ...purchaseOrders];
    save('orders', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
  };

  // Record one dated goods receipt against a PO; status is recomputed and stored
  // so other screens (e.g. Finance payables) stay in sync.
  const handleReceive = (v) => {
    if (receiveIndex === null) return;
    const o = purchaseOrders[receiveIndex];
    const receipts = [...(o.receipts || []), { date: v.date, amount: Number(v.amount) || 0, note: v.note || '' }];
    const rec = receipts.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const amt = Number(o.amount) || 0;
    const status = rec <= 0 ? 'Open' : rec < amt ? 'Partially Received' : 'Received';
    const next = purchaseOrders.map((p, i) => (i === receiveIndex ? { ...p, receipts, status } : p));
    save('orders', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
    setHistoryId(o.id); // open the history popup so the new dated receipt is visible immediately
  };

  const historyOrder = purchaseOrders.find((o) => o.id === historyId) || null;

  const receiveOrder = receiveIndex !== null ? purchaseOrders[receiveIndex] : null;
  const outstanding = receiveOrder ? Math.max(0, (Number(receiveOrder.amount) || 0) - receivedOf(receiveOrder)) : 0;
  const RECEIVE_FIELDS = [
    { name: 'date', label: 'Receipt Date', type: 'date', default: today, required: true },
    { name: 'amount', label: 'Amount Received Now (INR)', type: 'number', default: outstanding, required: true, helper: `Outstanding balance: ${formatCurrency(outstanding)}` },
    { name: 'note', label: 'Note (optional)', helper: 'e.g. GRN / invoice no., batch' },
  ];

  const total = purchaseOrders.reduce((s, o) => s + o.amount, 0);
  // Awaiting = anything not fully received and not cancelled (Open + Partially Received).
  const open = purchaseOrders.filter((o) => { const s = deriveStatus(o); return s === 'Open' || s === 'Partially Received'; });
  const received = purchaseOrders.filter((o) => deriveStatus(o) === 'Received').length;

  const bySupplier = {};
  purchaseOrders.forEach((o) => { bySupplier[o.supplier] = (bySupplier[o.supplier] || 0) + o.amount; });
  const supEntries = Object.entries(bySupplier).sort((a, b) => b[1] - a[1]);
  const spendOptions = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: CHART_FONT },
    plotOptions: { bar: { horizontal: true, borderRadius: 6, borderRadiusApplication: 'end', barHeight: '58%', distributed: true } },
    fill: { type: 'gradient', gradient: { shade: 'light', type: 'horizontal', shadeIntensity: 0.25, gradientToColors: undefined, opacityFrom: 1, opacityTo: 0.85 } },
    colors: ['#7c5cff', '#0ea5e9', '#10b981', '#f59e0b', '#d946ef'],
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: { categories: supEntries.map((s) => s[0]), labels: { formatter: (v) => `${Math.round(v / 1000)}k`, style: { fontSize: '11px' } } },
    yaxis: { labels: { style: { fontSize: '12px' } } },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
    grid: { borderColor: 'background.default' },
  };
  const spendSeries = [{ name: 'Spend', data: supEntries.map((s) => s[1]) }];

  const statusList = ['Received', 'Partially Received', 'Open', 'Cancelled'];
  const statusCounts = statusList.map((s) => purchaseOrders.filter((o) => deriveStatus(o) === s).length);
  const statusOptions = {
    chart: { type: 'donut', fontFamily: CHART_FONT, dropShadow: { enabled: true, top: 4, blur: 8, opacity: 0.12 } },
    labels: statusList,
    colors: statusList.map((s) => STATUS_COLORS[s]),
    legend: { position: 'bottom', fontSize: '12px' },
    dataLabels: { enabled: false },
    stroke: { width: 2 },
    plotOptions: { pie: { donut: { size: '70%', labels: { show: true, total: { show: true, label: 'Total POs', fontSize: '12px', color: 'text.secondary', formatter: () => String(purchaseOrders.length) } } } } },
  };

  return (
    <PageShell>
      <PageHeader
        title="Purchase"
        subtitle="Purchase orders & supplier management"
        actions={<Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setDialogOpen(true)}>New PO</Button>}
      />

      <FormDialog open={dialogOpen} title="New Purchase Order" fields={ADD_FIELDS} onClose={() => setDialogOpen(false)} onSubmit={handleAdd} submitLabel="Create PO" />

      <FormDialog
        open={receiveIndex !== null}
        title={receiveOrder ? `Receive Goods · ${receiveOrder.id}` : 'Receive Goods'}
        fields={RECEIVE_FIELDS}
        onClose={() => setReceiveIndex(null)}
        onSubmit={handleReceive}
        submitLabel="Record Receipt"
      />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, mb: 2.5 }}>
          <KpiCard label="PO Value" value={formatCurrency(total)} icon={Truck} tone="sky" />
          <KpiCard label="Awaiting Receipt" value={String(open.length)} icon={Clock} tone="warning" hint={`${formatCurrency(open.reduce((s, o) => s + Math.max(0, (Number(o.amount) || 0) - receivedOf(o)), 0))} balance`} />
          <KpiCard label="Received" value={`${received}/${purchaseOrders.length}`} icon={PackageCheck} tone="success" />
        </Box>

        {purchaseOrders.length === 0 ? (
          <Panel title="Purchase Orders" accent="linear-gradient(90deg,#0ea5e9,#38bdf8)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              No purchase orders yet. Click <strong>New PO</strong> to create one — it’s saved to ThingsBoard.
            </Typography>
          </Panel>
        ) : (
          <>
            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, mb: 2.5 }}>
              <Panel title="Spend by Supplier" subtitle="Purchase value per vendor" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
                <Box sx={{ px: 2, pb: 1 }}>
                  <ReactApexChart options={spendOptions} series={spendSeries} type="bar" height={260} />
                </Box>
              </Panel>

              <Panel title="PO Status Mix" subtitle="Receipt progress across orders" accent="linear-gradient(90deg,#0ea5e9,#38bdf8)">
                <Box sx={{ px: 2, pb: 2 }}>
                  <ReactApexChart options={statusOptions} series={statusCounts} type="donut" height={260} />
                </Box>
              </Panel>
            </Box>

            {open.length > 0 && (
              <Panel title="Awaiting Goods Receipt" subtitle="Open & partially-received POs to follow up — record receipts as goods arrive" accent="linear-gradient(90deg,#f59e0b,#fbbf24)" sx={{ mb: 2.5 }}>
                <Box sx={{ px: 2, pb: 1.5 }}>
                  {open.map((o, i) => {
                    const bal = Math.max(0, (Number(o.amount) || 0) - receivedOf(o));
                    const partial = receivedOf(o) > 0;
                    return (
                      <Box key={o.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: i < open.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(14,165,233,0.12)', color: '#0369a1', flexShrink: 0 }}>
                          <Clock size={17} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>{o.supplier}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                            {o.id} · ordered {o.date} · {o.items} item(s){partial ? ` · ${formatCurrency(receivedOf(o))} received` : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.primary' }}>{formatCurrency(bal)}</Typography>
                          <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>{partial ? 'balance due' : 'to receive'}</Typography>
                        </Box>
                        <Button size="small" variant="outlined" startIcon={<PackagePlus size={15} />} sx={{ textTransform: 'none', flexShrink: 0 }}
                          onClick={() => setReceiveIndex(purchaseOrders.indexOf(o))}>
                          Receive
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              </Panel>
            )}

            <Panel title="All Purchase Orders" subtitle="Click a row to view its goods-receipt history · use Receive to record arriving goods" accent="linear-gradient(90deg,#10b981,#34d399)">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>PO #</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Items</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseOrders.map((o, idx) => {
                      const status = deriveStatus(o);
                      const amt = Number(o.amount) || 0;
                      const canReceive = status !== 'Received' && status !== 'Cancelled';
                      return (
                        <TableRow key={o.id} hover sx={{ cursor: 'pointer' }} onClick={() => setHistoryId(o.id)}>
                          <TableCell sx={{ fontWeight: 600 }}>{o.id}</TableCell>
                          <TableCell>{o.supplier}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{o.date}</TableCell>
                          <TableCell align="right">{o.items}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(amt)}</TableCell>
                          <TableCell><StatusChip label={status} tone={statusTone[status]} /></TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            {canReceive ? (
                              <Tooltip title="Record goods receipt">
                                <Button size="small" variant="outlined" startIcon={<PackagePlus size={15} />} sx={{ textTransform: 'none' }}
                                  onClick={() => setReceiveIndex(idx)}>
                                  Receive
                                </Button>
                              </Tooltip>
                            ) : status === 'Received' ? (
                              <PackageCheck size={18} color="#10b981" />
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Panel>

            <Dialog open={historyId !== null} onClose={() => setHistoryId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
              {historyOrder && (() => {
                const rec = receivedOf(historyOrder);
                const amt = Number(historyOrder.amount) || 0;
                const receipts = historyOrder.receipts || [];
                return (
                  <>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 0.5 }}>
                      {historyOrder.supplier} · {historyOrder.id}
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 400 }}>Goods-receipt history</Typography>
                    </DialogTitle>
                    <DialogContent>
                      <Box sx={{ py: 1, px: 0.5 }}>
                        {receipts.length === 0 && (
                          <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 2 }}>
                            No goods received yet. Use the <strong>Receive</strong> button to record the first receipt.
                          </Typography>
                        )}
                        {receipts.map((r, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: i < receipts.length - 1 ? '1px solid #eef2f7' : 'none' }}>
                            <PackageCheck size={15} color="#10b981" />
                            <Typography sx={{ fontSize: 12.5, color: 'text.primary', minWidth: 90 }}>{r.date}</Typography>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#047857', minWidth: 110 }}>{formatCurrency(Number(r.amount) || 0)}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.disabled', flex: 1 }}>{r.note || ''}</Typography>
                          </Box>
                        ))}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 1.5, pt: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
                          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Received <b style={{ color: '#047857' }}>{formatCurrency(rec)}</b> of {formatCurrency(amt)}</Typography>
                          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Balance <b style={{ color: rec < amt ? '#b45309' : '#047857' }}>{formatCurrency(Math.max(0, amt - rec))}</b></Typography>
                        </Box>
                      </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                      {deriveStatus(historyOrder) !== 'Received' && deriveStatus(historyOrder) !== 'Cancelled' && (
                        <Button startIcon={<PackagePlus size={15} />} sx={{ textTransform: 'none' }}
                          onClick={() => { setReceiveIndex(purchaseOrders.indexOf(historyOrder)); setHistoryId(null); }}>
                          Record Receipt
                        </Button>
                      )}
                      <Button onClick={() => setHistoryId(null)} variant="contained" sx={{ textTransform: 'none' }}>Close</Button>
                    </DialogActions>
                  </>
                );
              })()}
            </Dialog>
          </>
        )}
      </LoadGate>
    </PageShell>
  );
}
